import { useTranslation } from 'react-i18next';
import { cn } from '@easymoving/ui';
import type { Item } from '@easymoving/types';
import { roomLabel } from '../../lib/taxonomy/options';

/**
 * Horizontal room switcher for the gallery — movers think room-by-room.
 * Shows the rooms that actually have items; hidden when there are fewer than
 * two distinct rooms (nothing to switch between).
 */
export function RoomTabs({
  items,
  value,
  onChange,
}: {
  items: Item[];
  value?: string;
  onChange: (room: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const rooms = [...new Set(items.map((i) => i.room).filter((r): r is string => !!r))];
  if (rooms.length < 2) return null;

  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 md:-mx-8 md:px-8 mt-2 pb-0.5">
      <Tab active={!value} onClick={() => onChange(undefined)}>
        {t('gallery.allRooms')}
      </Tab>
      {rooms.map((r) => (
        <Tab key={r} active={value === r} onClick={() => onChange(r)}>
          {roomLabel(r, t)}
        </Tab>
      ))}
    </div>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'shrink-0 rounded-full px-3.5 py-1.5 text-[14px] font-medium transition-colors',
        active ? 'bg-petrol text-white' : 'bg-surface border border-rule text-graphite hover:bg-surface-tint',
      )}
    >
      {children}
    </button>
  );
}
